import React, {
  useEffect,
  useRef,
  useState,
  memo,
  useMemo,
} from 'react';
import SvgIcon from '@home/components/SvgIcon';
import { Card, Icon } from 'tea-component';
import Answer from '../../../svg/answer-icon.svg';
import classNames from 'classnames';
import { useIntl, useLocation } from 'umi';
import { Collapse } from '@/pages/aigc/components/Collapse';
import { buriedBtnPoint } from '@home/utils/buriedPoint';
import { useLocalState } from '../../hooks/useLocalState';
import {
  SwitchAgentAccordion,
} from '@/pages/aigc/components/SwitchAgentAccordion';
import { MainContentCmp } from '.';
import { ThinkModeTextCmp } from './ThinkModeTextCmp';
import { useAccordionData } from '../../hooks/useAccordionData';
import { useStallByText } from '../../hooks/useStallByText';
import AnswerLoading from '../AnswerLoading';
import { StreamingText } from '@/pages/aigc/components/StreamingText';
import { isEmpty } from 'lodash-es';
import { TokenTotalCmp } from './TokenTotalCmp';
import { shouldSkipThinkCmpRender } from './thinkCmpMemo';
import CommonBubble from '@home/components/CommonBubble';
import BasicImage from '@home/components/BasicImage';
import { LANGUAGE_CN } from '@home/_config/config';
import { DATABRAIN_AGENT_ID } from '@/pages/aigc/pages/Agent/config/constant';
import { SKILL_AGENT_UUID } from '@/pages/aigc/pages/Agent/ChatPage/utils/submitHelpers';
import { useAgentModelsData } from '../../../AgentCollection/hooks';
import type { IAgentData } from '../../../AgentCollection/types';

/** ThinkCmp 组件 Props 接口 */
interface IThinkCmpProps {
  status: string;
  value: Array<{
    type: string;
    value?: unknown;
    new_agent?: string;
    input?: string;
    inputIndex?: number;
    thinkId?: string;
    tool_call_id?: string;
    previous_agent?: string;
    buried?: string;
  }>;
  isLastQA: boolean;
  streamingState: string;
  thinkTimeSeconds?: number;
  query: string;
  mode: string;
  modeId?: string;
  messageId: string;
  retrying: boolean;
  answer?: Array<{ type: string; value?: unknown; }>;
}

export const ThinkCmp = memo((props: IThinkCmpProps) => {
  const { status, value, isLastQA, streamingState, thinkTimeSeconds, query, mode, modeId, messageId, retrying } =
    props;
  const location = useLocation();
  const { sessionId = '', template } = (location as any)?.query || {};
  const isLoading = ['complete', 'rejected'].includes(streamingState) ? false : status === 'pending';
  const [isShowDetail, setIsShowDetail] = useState(isLoading);
  const { formatMessage, locale } = useIntl();
  const timeRef = useRef<ReturnType<typeof setTimeout> | null>(null); // (Code Review Fix #8)
  const firstFlag = useRef(false);
  const dsMode = template && template === 'dsReport';

  // 从 agent hook 中查找当前 modeId 对应的 agent 数据
  const { allAgents } = useAgentModelsData();
  const agentInfo = useMemo(() => {
    if (!allAgents?.length) {
      return { data: undefined, name: '' };
    }
    const agents = allAgents as IAgentData[];
    const agent =
      (modeId ? agents.find((a) => a.agentId === modeId) : undefined) ??
      agents.find((a) => a.agentId === DATABRAIN_AGENT_ID);
    return {
      data: agent,
      name: agent ? (locale === LANGUAGE_CN ? agent.name_cn : agent.name) : '',
    };
  }, [modeId, allAgents, locale]);
  // 使用 useLocalState 持久化已处理的 thinkId，避免页面切换时状态重置导致重复打点
  const [processedThinkIds, setProcessedThinkIds] = useLocalState<string[]>(
    `__dba_processed_think_ids_${messageId}__`, 
    []
  );
  // 专门处理switch_agent类型的埋点逻辑
  useEffect(() => {
    if (!value || !streamingState) return;
    
    const newProcessedIds: string[] = [];
    
    value.forEach((item) => {
      if (!item.thinkId) return; // 类型保护
      if (item.type === 'switch_agent' && !processedThinkIds.includes(item.thinkId)) {
        buriedBtnPoint({
          pageId: '700501',
          buttonId: '700501011',
          buttonName: 'Agent使用',
          extInfo: JSON.stringify({
            sessionId,
            message: query,
            agent: item?.new_agent || item?.previous_agent || '',
            from: 'agent'
          }),
          typeId: 'aigc',
        });
        newProcessedIds.push(item.thinkId); // 标记为已处理
      }
      if (item.type === "plan_json"  && !processedThinkIds.includes(item.thinkId)) { 
        buriedBtnPoint({
          pageId: '700501',
          buttonId: '700501013',
          buttonName: '游戏名称',
          extInfo: JSON.stringify({
            sessionId,
            message: item?.buried,
            from: 'agent'
          }),
          typeId: 'aigc',
        });
        newProcessedIds.push(item.thinkId); // 标记为已处理
      }
    });
    
    // 如果有新的已处理ID，更新状态
    if (newProcessedIds.length > 0) {
      setProcessedThinkIds(prev => [...prev, ...newProcessedIds]);
    }
  }, [value, query, sessionId, streamingState, processedThinkIds, setProcessedThinkIds]);

  // 只执行一次自动关闭
  useEffect(() => {
    if ((streamingState && !isLoading) && isLastQA && !firstFlag.current && !retrying) {
      timeRef.current = setTimeout(() => {
        setIsShowDetail(false);
        firstFlag.current = true;
      }, 2000);
    }
    return () => {
      if (timeRef.current) {
        clearTimeout(timeRef.current);
      }
    };
  }, [isLastQA, isLoading, streamingState, retrying]);
  
  const { thinkTime, thinkModeBlock, plannerBlock, accordionData, trailingInlineItems, tokenTotalBlock } = useAccordionData({
    value,
    isLoading,
    streamingState,
    thinkTimeSeconds,
    messageId
  });
  const thinkStalled = useStallByText(value, streamingState, { scope: 'think' });

  const displayText = formatMessage(
    {
      id: isLoading
        ? 'dba_agent_thinking'
        : dsMode
          ? 'dba_agent_plan_done'
          : `dba_agent_thinkover_${mode || 'default'}`,
    },
    { agentName: agentInfo.name || 'AI' },
  );

  return (
    <>
      <div className="dba-agent-cover-container">
        <CommonBubble content={modeId === SKILL_AGENT_UUID ? 'Skill Agent' : agentInfo.name} arrowPointAtCenter>
          <span style={{ display: 'inline-flex' }}>
            <BasicImage src={agentInfo.data?.icon || Answer} errorSrc={Answer} className="dba_agent_cover" />
          </span>
        </CommonBubble>
        <Card.Body
          style={{
            padding: '0px',
          }}
        >
          <div
            className={classNames('dba-thinking', {
              ['dba-thinking-over']: !isLoading,
            })}
            onClick={() => {
              if (isLoading) return;
              setIsShowDetail(!isShowDetail);
            }}
          >
            <div style={{ marginRight: '5px' }}>
              {displayText}
              {!isLoading && thinkTime && (
                <span>{` (${formatMessage({
                  id: 'dba_agent_time_spent',
                })}${thinkTime})`}</span>
              )}
            </div>
            {isLoading && <Icon type="loading" />}
            {!isLoading && (
              <div className="dba-thinking-process">
                <SvgIcon
                  type={isShowDetail ? 'point-top' : 'point-down'}
                ></SvgIcon>
              </div>
            )}
          </div>
        </Card.Body>
        {isLoading && <div className={'dba-archive-later-viewing'}>
          <SvgIcon type='alert-circle-tips' />
          <span className={'dba-archive-later-viewing-text'} data-locale-en={locale === 'en-US'}>
            {formatMessage({id: dsMode ? "dba_agent_thinking_ds" : 'dba_later_viewing_text'})}
          </span>
        </div>}
      </div>
      {isLoading && isEmpty(value) && <AnswerLoading />}
      <Collapse
        className='dba-system-container'
        isOpen={isShowDetail}
      >
        {thinkModeBlock?.value && (
          <ThinkModeTextCmp
            value={thinkModeBlock?.value}
            streamingState={streamingState}
          />
        )}
        {plannerBlock?.value && (
          <MainContentCmp
            value={plannerBlock?.value}
            className={classNames('dba-think-content-left dba-think-content-planner', {
              'dba-think-content-no-top': thinkModeBlock?.value
            })}
            streamingState={streamingState}
            messageId={messageId}
          />
        )}
        {(accordionData.length > 0 || trailingInlineItems.length > 0) && (
          <SwitchAgentAccordion
            dataSource={accordionData}
            trailingInlineItems={trailingInlineItems}
            messageId={messageId}
            defaultOpenFirst={streamingState === 'pending'}
            streamingState={streamingState}
            isLoading={isLoading}
            iconTypeOpen="point-top"
            iconTypeClose="point-down"
            scrollContainerClassName=".dba-chat-page"
            stickyMaxLines={5}
          />
        )}
        {isLoading && isLastQA && thinkStalled && (
          <StreamingText
            from="think"
            textColor="#838697"
            streamColor="#000000"
            fontSize="14px"
            duration={1.5}
            loop
            autoPlay
          />
        )}
        <TokenTotalCmp value={Number(tokenTotalBlock?.value)} />
      </Collapse>
    </>
  );
},
shouldSkipThinkCmpRender,
);

ThinkCmp.displayName = 'ThinkCmp';
